// client/src/components/pages/LexiStar/PlanBoard/PlanBoardMap.jsx
//
// The day's plan on one map (LS-09 §5.2, §6) on the shared LexiStarMap:
//  - each route a bold line coloured by its aggregate state — on plan glowing
//    blue, delayed orange, deviating magenta with an animated glow, not
//    started dashed grey; the focused route (hover / selection in the panel)
//    is emphasised and the others dimmed;
//  - trucks as small heading chevrons in their business-state colour;
//  - source ⬒ and destination ⬓ markers with counts;
//  - active advisories as a hatched band beside the route (or segment), with a
//    ⚠ marker — beside, so the route's state colour stays readable.
// The animation stops under prefers-reduced-motion (LS-09 §6). Everything
// drawn is also in the route panel (map content reachable via lists).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import { boundsFromLngLats } from '../../../map/geo';
import {
  routeFeatures,
  advisoryFeatures,
  truckItems,
  truckTone,
  truckTitle,
  siteMeta,
  boardPoints,
  ROUTE_STATES,
} from './planBoardLogic';

const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.0, zoom: 6 };
const ADVISORY = '#f59e0b';

const prefersReducedMotion = () => {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
};

/** 0.2 → 0.65 → 0.2 … at ~12 fps while something is deviating. */
function usePulse(active) {
  const [pulse, setPulse] = useState(0.45);
  useEffect(() => {
    if (!active || prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      setPulse(0.45);
      return undefined;
    }
    let raf = 0;
    let last = 0;
    const tick = (t) => {
      if (t - last > 80) {
        last = t;
        setPulse(0.2 + 0.45 * ((Math.sin(t / 380) + 1) / 2));
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [active]);
  return pulse;
}

function Chevron({ color, heading }) {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        transform: `rotate(${Number(heading) || 0}deg)`,
        filter: 'drop-shadow(0 0 1.5px #fff) drop-shadow(0 0 1px rgba(0,0,0,.4))',
      }}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M12 2 L20 21 L12 16 L4 21 Z" fill={color} stroke="#fff" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

export default function PlanBoardMap({
  routes = [],
  sites = [],
  advisories = [],
  focusId = null,
  fitKey = '',
  onSelectRoute,
  onOpenTrip,
  className = 'relative z-0 h-[420px] lg:h-[560px] rounded-xl overflow-hidden border border-slate-200',
}) {
  const mapRef = useRef(null);
  const anyDeviating = routes.some((r) => r.state === 'deviating');
  const pulse = usePulse(anyDeviating);

  const lines = useMemo(() => routeFeatures(routes, focusId), [routes, focusId]);
  const advisoryFC = useMemo(() => advisoryFeatures(advisories), [advisories]);
  const trucks = useMemo(() => truckItems(routes), [routes]);
  const bounds = useMemo(() => boundsFromLngLats(boardPoints(routes, sites)), [routes, sites]);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds, { padding: 56, duration: 0, maxZoom: 12 });
    } catch {
      /* map not ready */
    }
  }, [bounds]);

  // Refit when the day / filters change (fitKey), not on every live refresh.
  const fittedFor = useRef(null);
  useEffect(() => {
    if (!bounds || fittedFor.current === fitKey) return;
    fittedFor.current = fitKey;
    fit();
  }, [fitKey, bounds, fit]);

  const onLoad = (e) => {
    mapRef.current = e.target;
    fittedFor.current = fitKey;
    fit();
  };

  const onClick = (e) => {
    const f = e.features?.[0];
    if (f?.properties?.route_id) onSelectRoute?.(f.properties.route_id);
  };

  const dimmed = (on, off) => ['case', ['==', ['get', 'dim'], true], on, off];

  return (
    <div className={className}>
      <LexiStarMap
        className="absolute inset-0"
        initialViewState={DEFAULT_VIEW}
        defaultBasemap="street"
        navigationPosition="top-left"
        onLoad={onLoad}
        onClick={onClick}
        interactiveLayerIds={['pb-route-line', 'pb-route-dashed']}
        overlay={
          <div className="absolute top-3 right-3 z-[1000]">
            <button
              type="button"
              onClick={fit}
              className="bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium text-slate-700"
            >
              Fit
            </button>
          </div>
        }
      >
        {lines.features.length > 0 && (
          <Source id="pb-routes" type="geojson" data={lines}>
            <Layer
              id="pb-route-glow"
              type="line"
              filter={['!=', ['get', 'state'], 'not_started']}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 14,
                'line-blur': 9,
                'line-opacity': dimmed(0.06, [
                  'case',
                  ['==', ['get', 'state'], 'deviating'],
                  pulse,
                  0.32,
                ]),
              }}
            />
            <Layer
              id="pb-route-line"
              type="line"
              filter={['!=', ['get', 'state'], 'not_started']}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['case', ['==', ['get', 'focus'], true], 7.5, 5],
                'line-opacity': dimmed(0.28, 1),
              }}
            />
            <Layer
              id="pb-route-dashed"
              type="line"
              filter={['==', ['get', 'state'], 'not_started']}
              layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
              paint={{
                'line-color': ROUTE_STATES.not_started.hex,
                'line-width': ['case', ['==', ['get', 'focus'], true], 6, 4],
                'line-dasharray': [2, 1.6],
                'line-opacity': dimmed(0.3, 0.95),
              }}
            />
          </Source>
        )}
        {advisoryFC.features.length > 0 && (
          <Source id="pb-advisories" type="geojson" data={advisoryFC}>
            <Layer
              id="pb-adv-fill"
              type="fill"
              filter={['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]}
              paint={{ 'fill-color': ADVISORY, 'fill-opacity': 0.18 }}
            />
            <Layer
              id="pb-adv-outline"
              type="line"
              filter={['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]}
              paint={{ 'line-color': '#b45309', 'line-width': 1.5, 'line-dasharray': [1, 1] }}
            />
            <Layer
              id="pb-adv-base"
              type="line"
              filter={['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]]}
              layout={{ 'line-cap': 'butt' }}
              // Beside the route, not over it, so its state colour stays readable.
              paint={{
                'line-color': ADVISORY,
                'line-width': 6,
                'line-offset': 9,
                'line-opacity': 0.75,
              }}
            />
            <Layer
              id="pb-adv-hatch"
              type="line"
              filter={['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]]}
              layout={{ 'line-cap': 'butt' }}
              paint={{
                'line-color': '#78350f',
                'line-width': 6,
                'line-offset': 9,
                'line-dasharray': [0.4, 0.8],
              }}
            />
          </Source>
        )}
        {advisories
          .filter((a) => a.anchor)
          .map((a) => (
            <Marker key={`adv-${a.key}`} longitude={a.anchor.lon} latitude={a.anchor.lat}>
              <span
                title={`Advisory on ${a.route_name || 'route'}: ${a.title}`}
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-400 text-amber-950 text-sm font-bold shadow ring-2 ring-white"
              >
                ⚠
              </span>
            </Marker>
          ))}
        {sites
          .filter((s) => s.lon != null && s.lat != null)
          .map((s) => {
            const meta = siteMeta(s);
            return (
              <Marker
                key={`site-${s.location_id}`}
                longitude={s.lon}
                latitude={s.lat}
                anchor="bottom"
              >
                <span
                  title={`${meta.roleLabel}: ${s.name}${meta.detail ? ` — ${meta.detail}` : ''}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-surface text-[11px] font-semibold text-slate-800 shadow ring-1 ring-slate-300 whitespace-nowrap"
                >
                  <span aria-hidden="true" style={{ color: meta.color }}>
                    {meta.glyph}
                  </span>
                  {s.name}
                  <span className="tabular-nums rounded bg-slate-100 px-1 text-slate-700">
                    {meta.total}
                  </span>
                </span>
              </Marker>
            );
          })}
        {trucks.map((t) => (
          <Marker
            key={`truck-${t.trip_id || t.asset_key}`}
            longitude={t.lon}
            latitude={t.lat}
            onClick={(e) => {
              e?.originalEvent?.stopPropagation?.();
              if (t.trip_id) onOpenTrip?.(t.trip_id);
            }}
          >
            <div title={truckTitle(t)} style={{ cursor: t.trip_id ? 'pointer' : 'default' }}>
              <Chevron color={truckTone(t.stage).hex} heading={t.heading} />
            </div>
          </Marker>
        ))}
      </LexiStarMap>
    </div>
  );
}
